/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { FailoverResponse, MaintenanceResponse, ServiceStatus, UsageData } from "../Types";
import { getFailoverHistoryAPI, getMonitoringAPI, getServiceStatusAPI, getServicesUsageAPI } from "kontrolEdge/views/requests/request";

interface ManagerOverviewLoadingState {
  service: boolean;
  usage: boolean;
  failover: boolean;
  maintenance: boolean;
}

interface ManagerOverviewDataState {
  serviceStatus: ServiceStatus | null;
  usage: UsageData | null;
  failover: FailoverResponse | null;
  maintenance: MaintenanceResponse | null;
}

export function useManagerOverviewData(api: any, tenantId: number) {
  const [isServiceFetched, setIsServiceFetched] = useState(false);

  const [data, setData] = useState<ManagerOverviewDataState>({
    serviceStatus: null,
    usage: null,
    failover: null,
    maintenance: null,
  });

  const [loading, setLoading] = useState<ManagerOverviewLoadingState>({
    service: false,
    usage: false,
    failover: false,
    maintenance: false,
  });

  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    if (!tenantId) return;

    const controller = new AbortController();
    const { signal } = controller;

    //Mark all as loading
    setLoading({ service: true, usage: true, failover: true, maintenance: true });
    setIsServiceFetched(false);

    (async () => {
      try {
        const pService = getServiceStatusAPI(api, tenantId, { signal });
        const pUsage = getServicesUsageAPI(api, tenantId, { signal });
        const pFail  = getFailoverHistoryAPI(api, tenantId, { signal });
        const pMaint = getMonitoringAPI(api, tenantId, { signal });

        const [rService, rUsage, rFail, rMaint] = await Promise.allSettled([
          pService, pUsage, pFail, pMaint
        ]);

        // Extract values safely
        const serviceStatus =
          rService.status === "fulfilled" ? rService.value.data?.data ?? null : null;
        const usage =
          rUsage.status === "fulfilled" ? rUsage.value.data ?? null : null;
        const failover =
          rFail.status === "fulfilled" ? rFail.value.data?.data ?? null : null;
        const maintenance =
          rMaint.status === "fulfilled" ? rMaint.value.data?.data ?? null : null;

        // Single, clean state commit with the full payload
        if (!signal.aborted) {
          setData({ serviceStatus, usage, failover, maintenance });

          // Compute hasData from serviceStatus only
          const hs =
            !!serviceStatus &&
            (
              (serviceStatus.internet?.length ?? 0) > 0 ||
              (serviceStatus.datacenterInterconnect?.length ?? 0) > 0 ||
              (serviceStatus.cloudConnectivity?.length ?? 0) > 0 ||
              (serviceStatus.fwaas?.length ?? 0) > 0
            );

          setHasData(hs);
          setIsServiceFetched(true);
        }

        // Optional: log partial failures
        if (rService.status === "rejected") console.error("serviceStatus failed:", rService.reason);
        if (rUsage.status   === "rejected") console.error("usage failed:", rUsage.reason);
        if (rFail.status    === "rejected") console.error("failover failed:", rFail.reason);
        if (rMaint.status   === "rejected") console.error("maintenance failed:", rMaint.reason);
      } catch (err) {
        if (!signal.aborted) console.error("ManagerOverview parallel fetch error:", err);
      } finally {
        if (!signal.aborted) {
          setLoading({ service: false, usage: false, failover: false, maintenance: false });
        }
      }
    })();

    return () => controller.abort();
  }, [api, tenantId]);

  return { data, loading, hasData, isServiceFetched };
}

